import { useState } from 'react';
import type { ArtifactFeature } from '../types.ts';
import { MentionList } from './MentionList.tsx';
import { Highlight } from './Highlight.tsx';

/**
 * One vendor feature, expandable to its subfeatures + mentions. Used for the "not compared"
 * list and the vendor pivot's unified list — the cited feature in a cell expansion renders
 * inline instead (CellDetail) since it's already the open state.
 *
 * `open`/`onToggle` are optional: pass both for controlled mode (expand/collapse-all, v1.1),
 * omit both to keep the original uncontrolled per-item toggle. `highlightQuery` is likewise
 * optional and only passed by the filtered views.
 */
export function FeatureExpansion({
  feature,
  open: controlledOpen,
  onToggle,
  highlightQuery = '',
}: {
  feature: ArtifactFeature;
  open?: boolean;
  onToggle?: () => void;
  highlightQuery?: string;
}): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const toggle = onToggle ?? (() => setInternalOpen((o) => !o));

  return (
    <div className="feature-expansion">
      <button className="feature-expansion-toggle" onClick={toggle} aria-expanded={open}>
        <span className="disclosure" aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
        <Highlight text={feature.name} query={highlightQuery} />
        {feature.subfeatures.length > 0 && (
          <span className="sub-count muted"> ({feature.subfeatures.length} sub)</span>
        )}
      </button>
      {open && (
        <div className="feature-expansion-body">
          <p className="feature-product muted">{feature.product}</p>
          <MentionList mentions={feature.mentions} highlightQuery={highlightQuery} />
          {feature.subfeatures.map((s) => (
            <div key={s.name} className="subfeature">
              <strong>
                <Highlight text={s.name} query={highlightQuery} />
              </strong>
              <MentionList mentions={s.mentions} highlightQuery={highlightQuery} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
